import { FC, MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Tag } from '@monorepo/nova-ui';
import {
  AssetPrice,
  AssetSymbol,
  AssetTags,
  MarketDataGrid,
  SentimentBar,
} from '../../pinned-asset-overview-card/components';
import {
  StyledChipCell,
  StyledDataCell,
  StyledDataCellText,
  StyledExpandedContent,
  StyledExpandedLoadingContent,
  StyledMobileCaretButton,
  StyledMobileCurrencySection,
  StyledMobileMetricColumn,
  StyledMobileMetricEntry,
  StyledMobileMetricLabel,
  StyledMobileMetricsCollapse,
  StyledMobileMetricsSection,
  StyledMobileMetricSubLabel,
  StyledMobileMetricSubRow,
  StyledMobileMetricValue,
  StyledMobilePinButton,
  StyledMobileRow,
  StyledMobileTagGroup,
  StyledMobileTagLabel,
  StyledMobileTagsSection,
  StyledMobileTopSection,
  StyledNAText,
  StyledPinButton,
  StyledPinCell,
  StyledRow,
  StyledStatusCell,
  StyledSymbolCell,
  StyledSymbolText,
} from '../styled/community-trades-table.styled';
import type { TradeRowProps } from '../types';
import { TradeDirection, TradeSentiment } from '../types';
import { formatSymbolDisplay } from '../utils/forex-flags.utils';
import { ForexFlagIcon } from './forex-flag-icon';
import { StatusIcon } from './status-icon';

/**
 * Maps direction/sentiment values to Tag color props
 */
const getDirectionColor = (
  direction: TradeDirection,
): 'warning' | 'blue' | 'textThird' => {
  switch (direction) {
    case TradeDirection.Long:
    case TradeDirection.StrongLong:
      return 'warning';
    case TradeDirection.StrongShort:
    case TradeDirection.Short:
      return 'blue';
    default:
      return 'textThird';
  }
};

const getSentimentColor = (
  sentiment: TradeSentiment,
): 'warning' | 'blue' | 'textThird' => {
  switch (sentiment) {
    case TradeSentiment.Long:
    case TradeSentiment.StrongLong:
      return 'warning';
    case TradeSentiment.StrongShort:
    case TradeSentiment.Short:
      return 'blue';
    default:
      return 'textThird';
  }
};

/**
 * Formats a numeric value for display, or returns null for N/A
 */
const formatMetricValue = (value: number | null): string | null => {
  if (value === null) return null;

  const absValue = Math.abs(value);

  // Forex-like values (< 10): show 5 decimal places (e.g., 1.07200)
  if (absValue < 10) {
    return value.toFixed(5);
  }

  // Larger values: show 2 decimal places with thousands separator
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Maps direction/sentiment enum values to translation keys
 */
const DIRECTION_TRANSLATION_MAP: Record<TradeDirection, string> = {
  [TradeDirection.Long]: 'communityTradesTable.directions.long',
  [TradeDirection.Short]: 'communityTradesTable.directions.short',
  [TradeDirection.StrongShort]: 'communityTradesTable.directions.strongShort',
  [TradeDirection.Neutral]: 'communityTradesTable.directions.neutral',
  [TradeDirection.StrongLong]: 'communityTradesTable.directions.strongLong',
};

const SENTIMENT_TRANSLATION_MAP: Record<TradeSentiment, string> = {
  [TradeSentiment.Long]: 'communityTradesTable.sentiments.long',
  [TradeSentiment.Short]: 'communityTradesTable.sentiments.short',
  [TradeSentiment.StrongShort]: 'communityTradesTable.sentiments.strongShort',
  [TradeSentiment.Neutral]: 'communityTradesTable.sentiments.neutral',
  [TradeSentiment.StrongLong]: 'communityTradesTable.sentiments.strongLong',
};

/**
 * TradeRow Component
 * Renders a single data row in the community trades table.
 * On screens narrower than 900px a card-style mobile layout is used.
 */
export const TradeRow: FC<TradeRowProps> = ({
  row,
  isDetailLoading,
  onPinToggle,
  onRowClick,
}) => {
  const { t } = useTranslation('communityStats');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRowClick = useCallback(() => {
    onRowClick?.(row);
  }, [onRowClick, row]);

  const handleCaretClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setIsExpanded((prev) => {
        const willExpand = !prev;
        if (willExpand && !row.isActive) {
          onRowClick?.(row);
        }
        return willExpand;
      });
    },
    [onRowClick, row],
  );

  const handlePinClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onPinToggle?.(row);
    },
    [onPinToggle, row],
  );

  const {
    positionsLong,
    positionsShort,
    volumeLong,
    volumeShort,
    metric3Long,
    metric3Short,
  } = row.metrics;

  const metricValues = [
    positionsLong,
    positionsShort,
    volumeLong,
    volumeShort,
    metric3Long,
    metric3Short,
  ];

  const mobileMetricColumns = [
    {
      label: t('communityTradesTable.groups.aveEntry' as never),
      longValue: positionsLong,
      shortValue: positionsShort,
      hasDivider: true,
    },
    {
      label: t('communityTradesTable.groups.aveStopLoss' as never),
      longValue: volumeLong,
      shortValue: volumeShort,
      hasDivider: true,
    },
    {
      label: t('communityTradesTable.groups.aveTakeProfit' as never),
      longValue: metric3Long,
      shortValue: metric3Short,
      hasDivider: false,
    },
  ];

  // Prepare data for reused components from pinned-asset-overview-card
  const hasDetailedData = row.detailedData !== undefined;

  const symbolInfo = hasDetailedData
    ? {
        symbol: row.symbol,
        name: row.detailedData!.symbolName,
        abbreviation: row.abbreviation,
        category: row.detailedData!.category,
      }
    : undefined;

  const priceData = hasDetailedData
    ? {
        currentPrice: row.detailedData!.currentPrice,
        priceChange: row.detailedData!.priceChange,
        priceChangePercent: row.detailedData!.priceChangePercent,
        currency: row.detailedData!.currency,
      }
    : undefined;

  const sentimentData = hasDetailedData
    ? {
        assetSentimentPercent: row.detailedData!.assetSentimentPercent,
        marketSentimentPercent: row.detailedData!.marketSentimentPercent,
      }
    : undefined;

  const marketData = hasDetailedData
    ? {
        high24h: row.detailedData!.high24h,
        low24h: row.detailedData!.low24h,
        marketCap: 0,
        marketCapFormatted: row.detailedData!.marketCapFormatted,
        supply: 0,
        supplyFormatted: row.detailedData!.supplyFormatted,
        supplyUnit: '',
      }
    : undefined;

  const assetTags = hasDetailedData
    ? {
        volumeTag:
          row.sentiment === TradeSentiment.Long
            ? ('pinnedAssetOverview.tags.longVolume' as const)
            : row.sentiment === TradeSentiment.Short
              ? ('pinnedAssetOverview.tags.shortVolume' as const)
              : ('pinnedAssetOverview.tags.neutralVolume' as const),
        positionTag:
          row.direction === TradeDirection.Long
            ? ('pinnedAssetOverview.tags.long' as const)
            : row.direction === TradeDirection.Short
              ? ('pinnedAssetOverview.tags.short' as const)
              : row.direction === TradeDirection.StrongShort
                ? ('pinnedAssetOverview.tags.strongShort' as const)
                : ('pinnedAssetOverview.tags.neutralPositions' as const),
      }
    : undefined;

  // Format values for AssetPrice component
  const formattedPrice = priceData
    ? `$${priceData.currentPrice.toLocaleString('en-US')}`
    : '';
  const formattedChange = priceData
    ? `${priceData.priceChange >= 0 ? '+' : ''}$${Math.abs(priceData.priceChange).toLocaleString('en-US')}`
    : '';
  const formattedPercent = priceData
    ? `${priceData.priceChangePercent >= 0 ? '+' : ''}${priceData.priceChangePercent}%`
    : '';
  const changeColor: 'success' | 'error' =
    priceData && priceData.priceChange >= 0 ? 'success' : 'error';

  // Calculate sentiment bar widths
  const sentimentBarWidths = sentimentData
    ? {
        asset: sentimentData.assetSentimentPercent,
        market: sentimentData.marketSentimentPercent,
      }
    : { asset: 0, market: 0 };

  if (isMobile) {
    return (
      <StyledMobileRow
        isActive={row.isActive}
        onClick={handleRowClick}
        isExpanded={isExpanded}
      >
        <StyledMobilePinButton
          onClick={handlePinClick}
          isPinned={row.isPinned}
          aria-label={t('communityTradesTable.actions.togglePin' as never)}
          size="small"
        >
          <SystemIcon
            iconName={row.isPinned ? 'PushPinSimpleSlash' : 'PushPinSimple'}
            style={{ fontSize: 16 }}
          />
        </StyledMobilePinButton>

        <StyledMobileTopSection>
          <StyledMobileCurrencySection>
            <StyledMobileCaretButton
              isExpanded={isExpanded}
              onClick={handleCaretClick}
              size="small"
              aria-label={t('communityTradesTable.actions.togglePin' as never)}
            >
              <SystemIcon iconName="CaretDown" style={{ fontSize: 12 }} />
            </StyledMobileCaretButton>
            <ForexFlagIcon
              symbol={row.symbol}
              abbreviation={row.abbreviation}
            />
            <StyledSymbolText>
              {formatSymbolDisplay(row.symbol)}
            </StyledSymbolText>
          </StyledMobileCurrencySection>

          <StyledMobileTagsSection>
            <StyledMobileTagGroup>
              <StyledMobileTagLabel>
                {t('communityTradesTable.columns.positions' as never)}
              </StyledMobileTagLabel>
              <Tag
                label={t(DIRECTION_TRANSLATION_MAP[row.direction] as never)}
                color={getDirectionColor(row.direction)}
                size="small"
                sx={{
                  fontSize: '12px !important',
                  maxWidth: 'none',
                  width: 'max-content',
                  '& .MuiChip-label': {
                    overflow: 'visible',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </StyledMobileTagGroup>

            <StyledMobileTagGroup>
              <StyledMobileTagLabel>
                {t('communityTradesTable.columns.volume' as never)}
              </StyledMobileTagLabel>
              <Tag
                label={t(SENTIMENT_TRANSLATION_MAP[row.sentiment] as never)}
                color={getSentimentColor(row.sentiment)}
                size="small"
                sx={{
                  fontSize: '12px !important',
                  maxWidth: 'none',
                  width: 'max-content',
                  '& .MuiChip-label': {
                    overflow: 'visible',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </StyledMobileTagGroup>
          </StyledMobileTagsSection>
        </StyledMobileTopSection>

        <StyledMobileMetricsSection>
          {mobileMetricColumns.map((col) => (
            <StyledMobileMetricColumn
              key={col.label}
              hasDivider={col.hasDivider}
            >
              <StyledMobileMetricLabel>{col.label}</StyledMobileMetricLabel>
              <StyledMobileMetricSubRow>
                <StyledMobileMetricEntry>
                  <StyledMobileMetricSubLabel>
                    {t('communityTradesTable.columns.long' as never)}
                  </StyledMobileMetricSubLabel>
                  {formatMetricValue(col.longValue) !== null ? (
                    <StyledMobileMetricValue>
                      {formatMetricValue(col.longValue)}
                    </StyledMobileMetricValue>
                  ) : (
                    <StyledNAText>
                      {t('communityTradesTable.notAvailable' as never)}
                    </StyledNAText>
                  )}
                </StyledMobileMetricEntry>

                <StyledMobileMetricEntry>
                  <StyledMobileMetricSubLabel>
                    {t('communityTradesTable.columns.short' as never)}
                  </StyledMobileMetricSubLabel>
                  {formatMetricValue(col.shortValue) !== null ? (
                    <StyledMobileMetricValue>
                      {formatMetricValue(col.shortValue)}
                    </StyledMobileMetricValue>
                  ) : (
                    <StyledNAText>
                      {t('communityTradesTable.notAvailable' as never)}
                    </StyledNAText>
                  )}
                </StyledMobileMetricEntry>
              </StyledMobileMetricSubRow>
            </StyledMobileMetricColumn>
          ))}
        </StyledMobileMetricsSection>
        <StyledMobileMetricsCollapse in={isExpanded}>
          {hasDetailedData &&
          symbolInfo &&
          priceData &&
          sentimentData &&
          marketData &&
          assetTags ? (
            <StyledExpandedContent>
              <AssetSymbol symbol={symbolInfo} />
              <AssetPrice
                priceData={priceData}
                formattedPrice={formattedPrice}
                formattedChange={formattedChange}
                formattedPercent={formattedPercent}
                changeColor={changeColor}
              />
              <SentimentBar
                sentimentData={sentimentData}
                barWidths={sentimentBarWidths}
              />
              <MarketDataGrid marketData={marketData} />
              <AssetTags tags={assetTags} />
            </StyledExpandedContent>
          ) : isDetailLoading ? (
            <StyledExpandedLoadingContent>
              <CircularProgress size={24} />
            </StyledExpandedLoadingContent>
          ) : null}
        </StyledMobileMetricsCollapse>
      </StyledMobileRow>
    );
  }

  return (
    <StyledRow isActive={row.isActive} onClick={handleRowClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StyledStatusCell>
          {row.isActive ? (
            <StatusIcon
              status={row.status}
              abbreviation={row.abbreviation}
              isActive={row.isActive}
            />
          ) : (
            <ForexFlagIcon
              symbol={row.symbol}
              abbreviation={row.abbreviation}
            />
          )}
        </StyledStatusCell>

        <StyledSymbolCell>
          <StyledSymbolText>{formatSymbolDisplay(row.symbol)}</StyledSymbolText>
        </StyledSymbolCell>
      </div>

      <StyledChipCell>
        <Tag
          label={t(DIRECTION_TRANSLATION_MAP[row.direction] as never)}
          color={getDirectionColor(row.direction)}
          size="small"
          sx={{
            p: 0,
            fontSize: '12px !important',
            '& .MuiChip-label': { overflow: 'visible', textOverflow: 'clip' },
          }}
        />
      </StyledChipCell>

      <StyledChipCell>
        <Tag
          label={t(SENTIMENT_TRANSLATION_MAP[row.sentiment] as never)}
          color={getSentimentColor(row.sentiment)}
          size="small"
          sx={{
            p: 0,
            fontSize: '12px !important',
            '& .MuiChip-label': { overflow: 'visible', textOverflow: 'clip' },
          }}
        />
      </StyledChipCell>

      {metricValues.map((value, index) => {
        const formattedValue = formatMetricValue(value);
        return (
          <StyledDataCell key={index}>
            {formattedValue !== null ? (
              <StyledDataCellText>{formattedValue}</StyledDataCellText>
            ) : (
              <StyledNAText>
                {t('communityTradesTable.notAvailable' as never)}
              </StyledNAText>
            )}
          </StyledDataCell>
        );
      })}

      <StyledPinCell>
        <StyledPinButton
          onClick={handlePinClick}
          isPinned={row.isPinned}
          aria-label={t('communityTradesTable.actions.togglePin' as never)}
          size="small"
        >
          <SystemIcon
            iconName={row.isPinned ? 'PushPinSimpleSlash' : 'PushPinSimple'}
            style={{ fontSize: 16 }}
          />
        </StyledPinButton>
      </StyledPinCell>
    </StyledRow>
  );
};
